/**
 * IFRAP-AI.fr - History Page
 * Browse and manage all chroniques, rapports and Gamma exports
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { 
  FileText, Radio, BarChart3, ExternalLink, RefreshCw, 
  Calendar, User, Clock, Filter, Download, Presentation,
  ChevronDown, ChevronRight, Search
} from 'lucide-react';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, chronique, rapport
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/history/all?limit=100');
      setHistory(res.data.items || []);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = history.filter(item => {
    // Category filter
    if (filter !== 'all' && item.category !== filter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const title = (item.title || item.topic || '').toLowerCase();
      const media = (item.media || '').toLowerCase();
      const texte = (item.texte || item.content || '').toLowerCase();
      return title.includes(query) || media.includes(query) || texte.includes(query);
    }
    
    return true;
  });

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'chronique':
        return <Radio className="w-5 h-5 text-purple-400" />;
      case 'rapport':
        return <FileText className="w-5 h-5 text-blue-400" />;
      default:
        return <BarChart3 className="w-5 h-5 text-gray-400" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'chronique':
        return 'Chronique';
      case 'rapport':
        return 'Rapport';
      default:
        return 'Autre';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Historique</h1>
          <p className="text-gray-400 text-sm">Tous vos rapports et chroniques générés</p>
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="px-4 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white hover:border-gray-600 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Radio className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_chroniques || 0}</p>
                <p className="text-sm text-gray-500">Chroniques</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_rapports || 0}</p>
                <p className="text-sm text-gray-500">Rapports</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Presentation className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_gamma_exports || 0}</p>
                <p className="text-sm text-gray-500">Exports Gamma</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {Object.values(stats.chroniques_by_media || {}).reduce((a, b) => a + b, 0)}
                </p>
                <p className="text-sm text-gray-500">Total contenus</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 bg-[#1a1a2e] border border-gray-800 rounded-lg p-1">
          {['all', 'chronique', 'rapport'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f === 'all' ? 'Tout' : getCategoryLabel(f)}
            </button>
          ))}
        </div>
        
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1a1a2e] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">Aucun contenu dans l'historique</p>
            <p className="text-gray-600 text-sm mt-1">
              Les chroniques et rapports générés apparaîtront ici
            </p>
          </div>
        ) : (
          filteredHistory.map((item) => (
            <div
              key={item.id}
              className="bg-[#1a1a2e] border border-gray-800 rounded-lg overflow-hidden hover:border-gray-700 transition-colors"
            >
              {/* Header */}
              <div
                className="p-4 cursor-pointer flex items-center gap-4"
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              >
                <div className="flex-shrink-0">
                  {getCategoryIcon(item.category)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      item.category === 'chronique' 
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {getCategoryLabel(item.category)}
                    </span>
                    {item.media && (
                      <span className="text-xs text-gray-500">{item.media}</span>
                    )}
                    {item.gamma_url && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                        Gamma
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-white font-medium truncate">
                    {item.title || item.topic || 'Sans titre'}
                  </h3>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>{item.created_by_email?.split('@')[0] || 'Anonyme'}</span>
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  {expandedId === item.id ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>
              
              {/* Expanded content */}
              {expandedId === item.id && (
                <div className="border-t border-gray-800 p-4">
                  {/* Chronique specific */}
                  {item.category === 'chronique' && (
                    <div className="space-y-4">
                      {item.lancement && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-1">Lancement</h4>
                          <p className="text-gray-300 text-sm bg-black/30 p-3 rounded italic">
                            "{item.lancement}"
                          </p>
                        </div>
                      )}
                      {item.texte && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-1">Texte</h4>
                          <div className="text-gray-300 text-sm bg-black/30 p-3 rounded max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {item.texte}
                          </div>
                        </div>
                      )}
                      {item.relances?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-1">Relances</h4>
                          <ul className="text-gray-300 text-sm space-y-1">
                            {item.relances.map((r, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-gray-600">•</span>
                                <span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.duree_estimee && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-400">Durée estimée: {item.duree_estimee}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Rapport specific */}
                  {item.category === 'rapport' && (
                    <div className="space-y-4">
                      {item.content && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-1">Contenu</h4>
                          <div className="text-gray-300 text-sm bg-black/30 p-3 rounded max-h-64 overflow-y-auto whitespace-pre-wrap">
                            {item.content}
                          </div>
                        </div>
                      )}
                      {item.sources?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-1">Sources</h4>
                          <ul className="text-gray-400 text-sm space-y-1">
                            {item.sources.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-4 pt-4 border-t border-gray-800">
                    {item.gamma_url && (
                      <a
                        href={item.gamma_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Presentation className="w-4 h-4" />
                        Ouvrir dans Gamma
                      </a>
                    )}
                    <button
                      onClick={() => {
                        const text = item.texte || item.content || '';
                        navigator.clipboard.writeText(text);
                      }}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 flex items-center gap-2"
                    >
                      Copier le texte
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
